import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Heading,
  Portal,
  Text,
} from "@chakra-ui/react";
import { DataTable, StatusBadge, palette } from "./ui";
import { money } from "@/hooks/useNumberFormatter";

export type SegmentCustomerRow = {
  customer: string;
  contract: string;
  amount: number;
  segment: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  color: string;
  loading: boolean;
  rows: SegmentCustomerRow[];
  totalInSegment: number;
  truncated: boolean;
  onOpenProfile: (row: SegmentCustomerRow) => void;
};

/**
 * The tenants behind one segment, ordered by monthly rent. This is a six-month
 * behavioural history for contract decisions, not a collections queue — the
 * subtitle says so, because the two panels look alike and are used for
 * opposite things.
 */
const SegmentCustomersModal = ({
  open,
  onClose,
  title,
  color,
  loading,
  rows,
  totalInSegment,
  truncated,
  onOpenProfile,
}: Props): JSX.Element => (
  <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()} size="xl">
    <Portal>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="white"
          borderRadius="12px"
          maxW="880px"
          maxH="90vh"
          overflow="hidden"
          position="relative"
        >
          <Dialog.Header borderBottom="1px solid #EBECEF" px="6" py="4">
            <Box>
              <Dialog.Title>
                <Heading size="md" color={palette.text}>
                  Inquilinos: {title}
                </Heading>
              </Dialog.Title>
              <Text color={palette.muted} fontSize="13px" mt="1">
                Comportamiento de los ultimos 6 meses, ordenado por valor del
                canon. No es una lista de cobranza.
              </Text>
            </Box>
          </Dialog.Header>
          <Dialog.CloseTrigger asChild>
            <CloseButton
              position="absolute"
              top="4"
              right="4"
              onClick={onClose}
            />
          </Dialog.CloseTrigger>

          <Dialog.Body px="6" py="5">
            {loading ? (
              <Text color={palette.muted} fontSize="13px" py={6}>
                Cargando inquilinos...
              </Text>
            ) : rows.length === 0 ? (
              <Text color={palette.muted} fontSize="13px" py={6}>
                No hay inquilinos en este segmento.
              </Text>
            ) : (
              <>
                <Box maxH="55vh" overflowY="auto">
                  <DataTable
                    headers={[
                      "Inquilino",
                      "Contrato",
                      "Canon",
                      "Categoria",
                      "",
                    ]}
                    rows={rows.map((row, index) => [
                      row.customer || "Sin nombre",
                      row.contract,
                      money(row.amount),
                      <StatusBadge
                        key={`${row.contract}-${index}`}
                        color={color}
                      >
                        {title}
                      </StatusBadge>,
                      <Button
                        key={`${row.contract}-${index}-perfil`}
                        size="xs"
                        variant="outline"
                        onClick={() => onOpenProfile(row)}
                      >
                        Ver perfil
                      </Button>,
                    ])}
                  />
                </Box>
                <Text color={palette.muted} fontSize="11px" mt={3}>
                  {truncated
                    ? `Mostrando los ${rows.length} de mayor canon, de ${totalInSegment} inquilinos en este segmento.`
                    : `${totalInSegment} inquilinos en este segmento.`}
                </Text>
              </>
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Portal>
  </Dialog.Root>
);

export default SegmentCustomersModal;
